import { useState } from 'react'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  HouseStudyDayGlobalFormContainer,
  HouseStudyDayGlobalFormContainerProps,
} from './components/form-container/HouseStudyDayGlobalFormContainer'
import { HouseStudyDayGlobalIsEnabledField } from './components/is-enabled-field/HouseStudyDayGlobalIsEnabledField'
import { HouseStudyDayGlobalOptionsFields } from './components/options-fields/HouseStudyDayGlobalOptionsFields'
import {
  computeHouseStudyDayGlobalDefaultValues,
  computeHouseStudyDayGlobalInputs,
} from './helpers/compute-house-study-day-global'
import { HouseStudyDayGlobalDto } from '../../../../../../lib/graphql'
import { HouseOptionsFormLayoutProps } from '../../../HouseOptionsForm'

export type HouseStudyDayGlobalFormProps = {
  updateStudyDayGlobal: {
    mutate: (formData: HouseStudyDayGlobalDto) => void
    isLoading: boolean
  }
}
export const HouseStudyDayGlobalForm = ({
  handleOnExpand,
  house,
  isDisabled,
  isOpen,
  layoutProps,
  updateStudyDayGlobal,
}: HouseStudyDayGlobalFormProps & HouseOptionsFormLayoutProps) => {
  const [hasRequestBeenSent, setHasRequestBeenSent] = useState<boolean>(false)

  const { currencyArea, options } = house

  // Infer GQL Union type
  if (options.__typename !== 'HouseOptionsGlobal') {
    return null
  }

  const { studyDay } = options

  const methods = useForm<HouseStudyDayGlobalDto>({
    defaultValues: computeHouseStudyDayGlobalDefaultValues(studyDay),
  })
  const {
    control,
    watch,
    reset,
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
  } = methods

  const { mutate } = updateStudyDayGlobal

  const isEnabled = watch('isEnabled')

  const timeOutRequest = () => {
    setHasRequestBeenSent(true)
    setTimeout(() => setHasRequestBeenSent(false), 2000)
  }

  const onSubmit = (formData: HouseStudyDayGlobalDto) => {
    const inputs = computeHouseStudyDayGlobalInputs({ ...formData })

    mutate({ ...inputs })

    reset(formData)

    timeOutRequest()
  }

  const isFieldDisabled = isDisabled || hasRequestBeenSent

  const formContainerProps: Omit<
    HouseStudyDayGlobalFormContainerProps,
    'children'
  > = {
    handleOnExpand,
    isOpen,
    layoutProps,
    submitButtons: {
      confirm: hasRequestBeenSent,
      disabled: isFieldDisabled || !isDirty || !isValid,
      isLoading: isSubmitting || updateStudyDayGlobal.isLoading,
      onSubmit: handleSubmit(onSubmit),
      isValid,
    },
  }

  return (
    <FormProvider {...methods}>
      <HouseStudyDayGlobalFormContainer {...formContainerProps}>
        <HouseStudyDayGlobalIsEnabledField
          control={control}
          isFieldDisabled={isFieldDisabled}
        />

        {isEnabled ? (
          <HouseStudyDayGlobalOptionsFields
            control={control}
            currencyArea={currencyArea}
            isFieldDisabled={isFieldDisabled}
          />
        ) : null}
      </HouseStudyDayGlobalFormContainer>
    </FormProvider>
  )
}
